import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { fleetKeys } from '@/hooks/fleet-keys';
import { apiClient, fleetPath } from '@/lib/api-client';

export interface BulkProvisionInput {
  /** Audit reason — required (recorded in the activity log). */
  reason: string;
  /** Optional: limit to a single role. */
  role?: string;
  /** Optional: limit to a single ISO-3166 country code. */
  region_country?: string;
}

export interface BulkProvisionResult {
  name: string;
  role: string;
  status: 'dispatched' | 'skipped' | 'failed';
  task_id?: number;
  error?: string;
}

export interface BulkProvisionResponse {
  /** Eligible servers considered (excludes decommissioned). */
  total: number;
  dispatched: number;
  skipped: number;
  failed: number;
  results: BulkProvisionResult[];
}

/**
 * POST /servers/bulk-provision — re-provision every server except decommissioned.
 * Each server runs through the same per-role provision path on the backend.
 */
export function useBulkProvisionServers(): UseMutationResult<
  BulkProvisionResponse,
  Error,
  BulkProvisionInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) =>
      apiClient.post<BulkProvisionResponse>(fleetPath('/servers/bulk-provision'), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetKeys.all });
    },
  });
}
